import { useState, useEffect, createContext, useContext, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';

interface Profile {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  membership_plan: string; // admin, student, mastery, or legacy values
  membership_status: 'active' | 'canceled' | 'past_due';
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  adminCheckComplete: boolean;
  checkSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to wrap promises with a timeout
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`Operation timed out after ${ms}ms`)), ms)
    ),
  ]);
};

// Admin hint helpers - uses sessionStorage for session-level caching
const getAdminHint = (): boolean => {
  try {
    return sessionStorage.getItem('adminHint') === 'true';
  } catch {
    return false;
  }
};

const setAdminHint = (isAdmin: boolean): void => {
  try {
    if (isAdmin) {
      sessionStorage.setItem('adminHint', 'true');
    } else {
      sessionStorage.removeItem('adminHint');
    }
  } catch {
    // Ignore storage errors
  }
};

const clearAdminHint = (): void => {
  try {
    sessionStorage.removeItem('adminHint');
  } catch {
    // Ignore storage errors
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialize isAdmin from hint for optimistic rendering
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(() => getAdminHint()); // Optimistic UI hint only; route guards must wait for adminCheckComplete
  const [adminCheckComplete, setAdminCheckComplete] = useState(false); // Never trust sessionStorage for privilege gating
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const verifiedUserIdRef = useRef<string | null>(null); // Track which user we've verified

  const checkAdminRole = useCallback(async (userId: string, retryCount = 0): Promise<void> => {
    // Skip re-check if we've already verified this user
    if (verifiedUserIdRef.current === userId && adminCheckComplete) {
      console.log('[useAuth] Skipping admin check - already verified for user:', userId);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle();
      
      if (error) {
        console.error('[useAuth] Admin check error:', error);
        // Retry once after 500ms if first attempt fails (reduced from 1s)
        if (retryCount < 1) {
          console.log('[useAuth] Retrying admin check in 500ms...');
          await new Promise(resolve => setTimeout(resolve, 500));
          return checkAdminRole(userId, retryCount + 1);
        }
        // After retry, set as non-admin
        setIsAdmin(false);
        setAdminHint(false);
        setAdminCheckComplete(true);
        verifiedUserIdRef.current = userId;
        return;
      }
      
      const adminStatus = !!data;
      console.log('[useAuth] Admin check result:', adminStatus);
      
      // Set BOTH together atomically to prevent race conditions
      setIsAdmin(adminStatus);
      setAdminHint(adminStatus); // Cache in sessionStorage
      setAdminCheckComplete(true);
      verifiedUserIdRef.current = userId;
    } catch (err) {
      console.error('[useAuth] Admin check exception:', err);
      // Retry once on exception
      if (retryCount < 1) {
        console.log('[useAuth] Retrying admin check after exception...');
        await new Promise(resolve => setTimeout(resolve, 500));
        return checkAdminRole(userId, retryCount + 1);
      }
      // Set BOTH together even on error
      setIsAdmin(false);
      setAdminHint(false);
      setAdminCheckComplete(true);
      verifiedUserIdRef.current = userId;
    }
  }, [adminCheckComplete]);

  const fetchProfile = useCallback(async (userId: string, retryCount = 0): Promise<Profile | null> => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('[useAuth] fetchProfile error:', error);
        // Retry once after 1 second if first attempt fails
        if (retryCount < 1) {
          console.log('[useAuth] Retrying profile fetch in 1s...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchProfile(userId, retryCount + 1);
        }
        return null;
      }
      
      if (data) {
        setProfile(data as Profile);
        return data as Profile;
      }
      return null;
    } catch (err) {
      console.error('[useAuth] fetchProfile exception:', err);
      // Retry once on exception
      if (retryCount < 1) {
        console.log('[useAuth] Retrying profile fetch after exception...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchProfile(userId, retryCount + 1);
      }
      return null;
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!session) return;
    
    try {
      console.log('[useAuth] Checking subscription status...');
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('[useAuth] Error checking subscription:', error);
        // If we get a 401, the user's JWT is invalid (user deleted) - sign out
        if (error.message?.includes('401') || error.message?.includes('Authentication required')) {
          console.warn('[useAuth] User session invalid, signing out...');
          await supabase.auth.signOut();
          setUser(null);
          setSession(null);
          setProfile(null);
          setIsAdmin(false);
          setAdminCheckComplete(true);
          clearAdminHint();
        }
        return;
      }
      
      console.log('[useAuth] Subscription check result:', data);
      
      // Refresh profile to get updated membership data
      if (session.user) {
        await fetchProfile(session.user.id);
      }
    } catch (err) {
      console.error('[useAuth] Failed to check subscription:', err);
    }
  }, [session, fetchProfile]);

  // Auto-sync subscription on login and periodically
  useEffect(() => {
    let mounted = true;

    // CRITICAL: Failsafe timeout - NEVER let loading hang more than 4 seconds (reduced from 8s)
    const failsafeTimeout = setTimeout(() => {
      if (mounted && loading) {
        console.warn('[useAuth] Failsafe: forcing loading=false after timeout');
        setLoading(false);
        setAdminCheckComplete(true);
      }
    }, 4000);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!mounted) return;
        console.log('[useAuth] Auth state change:', event, 'sessionInitialized:', sessionInitialized);
        
        // Skip duplicate processing - if initSession already handled this session
        if (sessionInitialized && (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
          console.log('[useAuth] Skipping duplicate session processing for:', event);
          setLoading(false);
          return;
        }
        
        setSession(newSession);
        setUser(newSession?.user ?? null);
        
        if (newSession?.user) {
          try {
            // CRITICAL: Check admin role FIRST with timeout (reduced to 2s) - this determines if we skip other checks
            await withTimeout(checkAdminRole(newSession.user.id), 2000).catch(err => {
              console.error('[useAuth] Admin check failed/timed out in listener:', err);
              setIsAdmin(getAdminHint()); // Fall back to hint
              setAdminCheckComplete(true);
            });
            
            // Then fetch profile with timeout (non-blocking - admin already determined)
            withTimeout(fetchProfile(newSession.user.id), 5000).catch(err => {
              console.error('[useAuth] Profile fetch failed/timed out in listener:', err);
            });
          } catch (err) {
            console.error('[useAuth] Auth state processing failed:', err);
            setAdminCheckComplete(true);
          }
          
          // Auto-sync subscription on login (non-blocking)
          if (event === 'SIGNED_IN') {
            console.log('[useAuth] User signed in, checking subscription...');
            supabase.functions.invoke('check-subscription').then(async () => {
              if (mounted) {
                await fetchProfile(newSession.user.id).catch(() => null);
              }
            }).catch(err => {
              console.error('[useAuth] Subscription check failed:', err);
            });
          }
        } else {
          setProfile(null);
          setIsAdmin(false);
          setAdminCheckComplete(true);
        }
        
        // CRITICAL: Always set loading to false after auth state is processed
        setLoading(false);
      }
    );

    // THEN check for existing session
    const initSession = async () => {
      try {
        const { data: { session: existingSession }, error } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (error) {
          console.error('[useAuth] getSession error:', error);
          setLoading(false);
          setAdminCheckComplete(true);
          setSessionInitialized(true);
          return;
        }
        
        console.log('[useAuth] Initial session check:', !!existingSession);
        setSession(existingSession);
        setUser(existingSession?.user ?? null);
        
        if (existingSession?.user) {
          // Verify the user still exists by fetching their profile first
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', existingSession.user.id)
            .maybeSingle();
          
          // If profile doesn't exist, user was deleted - sign out
          if (profileError || !profileData) {
            console.warn('[useAuth] User profile not found, signing out stale session...');
            await supabase.auth.signOut();
            setUser(null);
            setSession(null);
            setProfile(null);
            setIsAdmin(false);
            setAdminCheckComplete(true);
            clearAdminHint();
            setSessionInitialized(true);
            setLoading(false);
            return;
          }
          
          // Profile exists, set it
          setProfile(profileData as Profile);
          
          try {
            // Check admin role with timeout (reduced to 2s)
            await withTimeout(checkAdminRole(existingSession.user.id), 2000).catch(err => {
              console.error('[useAuth] Admin check failed/timed out:', err);
              setIsAdmin(getAdminHint()); // Fall back to hint
              setAdminCheckComplete(true);
            });
          } catch (err) {
            console.error('[useAuth] Session processing failed:', err);
            setAdminCheckComplete(true);
          }
          
          // Auto-sync subscription on page load (non-blocking)
          console.log('[useAuth] Session found, checking subscription...');
          supabase.functions.invoke('check-subscription').then(async () => {
            if (mounted) await fetchProfile(existingSession.user.id).catch(() => {});
          }).catch(err => {
            console.error('[useAuth] Subscription check failed:', err);
          });
        } else {
          setAdminCheckComplete(true);
        }
        
        // Mark session as initialized to prevent duplicate processing by onAuthStateChange
        setSessionInitialized(true);
      } catch (err) {
        console.error('[useAuth] initSession failed:', err);
        setSessionInitialized(true);
      } finally {
        // CRITICAL: Always set loading to false, no matter what
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    return () => {
      mounted = false;
      clearTimeout(failsafeTimeout);
      subscription.unsubscribe();
    };
  }, [fetchProfile, checkAdminRole]); // Removed 'loading' - should only run once on mount

  // Periodic subscription sync (every 60 seconds)
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(async () => {
      console.log('[useAuth] Periodic subscription check...');
      try {
        await supabase.functions.invoke('check-subscription');
        if (session.user) {
          await fetchProfile(session.user.id);
        }
      } catch (err) {
        console.error('[useAuth] Periodic subscription check failed:', err);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [session, fetchProfile]);


  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName || email.split('@')[0],
        },
      },
    });
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Update last_login_at on successful sign in
    if (!error && data.user) {
      trackEvent('login_success', { method: 'email' });
      await supabase
        .from('profiles')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', data.user.id);
    } else if (error) {
      trackEvent('login_failed', { error: error.message });
    }
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    trackEvent('sign_out');
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    setIsAdmin(false);
    setAdminCheckComplete(false);
    verifiedUserIdRef.current = null;
    clearAdminHint();
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      loading,
      signUp,
      signIn,
      signOut,
      isAdmin,
      adminCheckComplete,
      checkSubscription,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    // Return a safe default during initial render or when outside provider
    // This prevents crashes during HMR or edge cases
    return {
      user: null,
      session: null,
      profile: null,
      loading: true,
      signUp: async () => ({ error: new Error('Auth not initialized') }),
      signIn: async () => ({ error: new Error('Auth not initialized') }),
      signOut: async () => {},
      isAdmin: false,
      adminCheckComplete: false,
      checkSubscription: async () => {},
    };
  }
  return context;
}
